import type { Scenario, Scene, SituationSortCategory, SituationSortGameScene } from '../../types';

function isSortGame(scene: Scene): scene is SituationSortGameScene {
  return scene.type === 'situationSortGame';
}

function mapScenes(scenes: Scene[], mapper: (scene: Scene) => Scene): Scene[] {
  return scenes.map((scene) => {
    let next = mapper(scene);
    if (next.type === 'choice') {
      next = {
        ...next,
        options: next.options.map((option) => ({
          ...option,
          scenes: option.scenes ? mapScenes(option.scenes, mapper) : option.scenes,
        })),
      };
    }
    return next;
  });
}

function updateSceneById(
  scenes: Scene[],
  sceneId: string,
  updater: (scene: Scene) => Scene
): Scene[] {
  return mapScenes(scenes, (scene) => (scene.id === sceneId ? updater(scene) : scene));
}

export function collectSortGames(scenes: Scene[]): SituationSortGameScene[] {
  const result: SituationSortGameScene[] = [];
  for (const scene of scenes) {
    if (isSortGame(scene)) result.push(scene);
    if (scene.type === 'choice') {
      for (const option of scene.options) {
        if (option.scenes) result.push(...collectSortGames(option.scenes));
      }
    }
  }
  return result;
}

export function collectCategoriesFromScenes(scenario: Scenario): SituationSortCategory[] {
  const byId = new Map<string, SituationSortCategory>();
  for (const game of collectSortGames(scenario.scenes)) {
    for (const category of game.categories) {
      byId.set(category.id, { ...category });
    }
  }
  return [...byId.values()];
}

export function bootstrapSituationSortCategories(scenario: Scenario): Scenario {
  const existing = scenario.metadata.situationSortCategories;
  if (existing && existing.length > 0) return scenario;
  const collected = collectCategoriesFromScenes(scenario);
  if (collected.length === 0) return scenario;
  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      situationSortCategories: collected,
    },
  };
}

function upsertCategoryInMetadata(
  scenario: Scenario,
  category: SituationSortCategory
): Scenario {
  const existing = scenario.metadata.situationSortCategories ?? [];
  const index = existing.findIndex((entry) => entry.id === category.id);
  const situationSortCategories =
    index >= 0
      ? existing.map((entry, i) => (i === index ? { ...category } : entry))
      : [...existing, { ...category }];
  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      situationSortCategories,
    },
  };
}

export function upsertGlobalCategory(
  scenario: Scenario,
  category: SituationSortCategory
): Scenario {
  let next = upsertCategoryInMetadata(scenario, category);
  next = {
    ...next,
    scenes: mapScenes(next.scenes, (scene) => {
      if (!isSortGame(scene)) return scene;
      return {
        ...scene,
        categories: scene.categories.map((entry) =>
          entry.id === category.id ? { ...category } : entry
        ),
      };
    }),
  };
  return next;
}

export function isCategoryInUse(scenario: Scenario, categoryId: string): boolean {
  for (const game of collectSortGames(scenario.scenes)) {
    if (game.categories.some((category) => category.id === categoryId)) return true;
    if (game.situations.some((situation) => situation.expectedCategoryId === categoryId)) {
      return true;
    }
  }
  return false;
}

export function removeGlobalCategory(
  scenario: Scenario,
  categoryId: string
): Scenario | null {
  if (isCategoryInUse(scenario, categoryId)) return null;
  const existing = scenario.metadata.situationSortCategories ?? [];
  if (!existing.some((category) => category.id === categoryId)) return scenario;
  return {
    ...scenario,
    metadata: {
      ...scenario.metadata,
      situationSortCategories: existing.filter((category) => category.id !== categoryId),
    },
  };
}

export function addGlobalCategory(
  scenario: Scenario,
  category: SituationSortCategory
): Scenario {
  return upsertGlobalCategory(scenario, category);
}

export function setGameCategories(
  scenario: Scenario,
  sceneId: string,
  categories: SituationSortCategory[]
): Scenario {
  let next = scenario;
  for (const category of categories) {
    next = upsertGlobalCategory(next, category);
  }
  return {
    ...next,
    scenes: updateSceneById(next.scenes, sceneId, (scene) => {
      if (!isSortGame(scene)) return scene;
      return { ...scene, categories };
    }),
  };
}

export function addCategoryToGame(
  scenario: Scenario,
  sceneId: string,
  category: SituationSortCategory
): Scenario {
  let next = upsertGlobalCategory(scenario, category);
  return {
    ...next,
    scenes: updateSceneById(next.scenes, sceneId, (scene) => {
      if (!isSortGame(scene)) return scene;
      if (scene.categories.some((entry) => entry.id === category.id)) return scene;
      return { ...scene, categories: [...scene.categories, { ...category }] };
    }),
  };
}

export function addCategoryFromGlobalToGame(
  scenario: Scenario,
  sceneId: string,
  categoryId: string
): Scenario {
  const global = scenario.metadata.situationSortCategories?.find(
    (category) => category.id === categoryId
  );
  if (!global) return scenario;
  return addCategoryToGame(scenario, sceneId, global);
}

export function updateCategoryLabel(
  scenario: Scenario,
  sceneId: string,
  categoryId: string,
  label: string
): Scenario {
  const global = scenario.metadata.situationSortCategories?.find(
    (category) => category.id === categoryId
  );
  const category = global ? { ...global, label } : { id: categoryId, label };
  const synced = upsertGlobalCategory(scenario, category);
  return {
    ...synced,
    scenes: updateSceneById(synced.scenes, sceneId, (scene) => {
      if (!isSortGame(scene)) return scene;
      return {
        ...scene,
        categories: scene.categories.map((entry) =>
          entry.id === categoryId ? { ...entry, label } : entry
        ),
      };
    }),
  };
}
